// Fatigue Management — the three-layer stakeholder dashboard.
//
// Layer 1 Command (board) · Layer 2 Operations (managers) · Layer 3 Detail (OH).
// Built from "PEAK4 Fatigue Management Dashboard.pptx". Currently driven by
// representative mock data (see ../data/mockData.ts) pending the algorithm
// engine endpoints for FED / LRM / CDI / PDC.

import React, { useState } from "react";
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { CommandView } from "../components/CommandView";
import { OperationsView } from "../components/OperationsView";
import { DetailView } from "../components/DetailView";

type Layer = 0 | 1 | 2;

const LAYERS = [
  { label: "Command", caption: "Board" },
  { label: "Operations", caption: "Managers" },
  { label: "Detail", caption: "Occupational health" },
];

export const FatigueManagement: React.FC = () => {
  const theme = useTheme();
  const [layer, setLayer] = useState<Layer>(0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Hero header */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            color: theme.palette.getContrastText(theme.palette.primary.main),
          }}
        >
          {/* decorative orbs */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: alpha("#FFFFFF", 0.12),
              filter: "blur(8px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -80,
              right: 120,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: alpha("#FFFFFF", 0.08),
            }}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <InsightsRoundedIcon />
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.9 }}>
              Fatigue Management
            </Typography>
          </Stack>
          <Typography variant="h3" fontWeight={900} sx={{ maxWidth: 720 }}>
            Fatigue Management
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, maxWidth: 720, opacity: 0.92 }}>
            FED, LRM, CDI and PDC across three stakeholder layers — Command,
            Operations and Detail.
          </Typography>
        </Box>

        {/* Layer switcher */}
        <Box
          sx={{
            borderRadius: 3,
            p: 0.5,
            display: "inline-flex",
            alignSelf: "flex-start",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Tabs
            value={layer}
            onChange={(_, v) => setLayer(v as Layer)}
            sx={{
              minHeight: 0,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                minHeight: 0,
                textTransform: "none",
                borderRadius: 2,
                px: 2.5,
                py: 1,
                transition: "all 0.2s ease",
              },
              "& .Mui-selected": {
                bgcolor: "background.paper",
                boxShadow: theme.shadows[3],
              },
            }}
          >
            {LAYERS.map((l, i) => (
              <Tab
                key={l.label}
                value={i}
                label={
                  <Stack alignItems="center" spacing={0}>
                    <Typography variant="body2" fontWeight={800}>
                      {l.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {l.caption}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>

        {layer === 0 && <CommandView />}
        {layer === 1 && <OperationsView />}
        {layer === 2 && <DetailView />}
      </Stack>
    </Box>
  );
};
